<template>
    <div class="kgr-row">
        <nav class="kgr-pagination">
            <a
                v-if="allowMultiple"
                class="kgr-pagination__item kgr-pagination__item_custom"
                @click="selectAll()"
            >
                {{ $t('Alle') }}
            </a>
            <a
                v-if="needsPrev"
                class="kgr-pagination__item kgr-pagination__item_custom"
                :class="{'is-active': isSelected(allOptions[0]) }"
                :title="title"
                @click="selectFirst()"
            >
                {{ allOptions[0].toString() }}
            </a>
            <span
                v-if="needsPrev"
                class="kgr-pagination__item kgr-pagination__item_nav"
                @click="prev()"
            >
                <kgr-icon icon-class="arrow-left" />
            </span>
            <a
                v-for="(a,b) in options"
                :key="b"
                class="kgr-pagination__item kgr-pagination__item_custom"
                :class="{'is-active': isSelected(a) }"
                :title="title"
                @click="select(a)"
            >
                {{ a.toString() }}
            </a>
            <span
                v-if="needsNext"
                class="kgr-pagination__item kgr-pagination__item_nav"
                @click="next()"
            >
                <kgr-icon icon-class="arrow-right" />
            </span>
            <a
                v-if="needsNext"
                class="kgr-pagination__item kgr-pagination__item_custom"
                :class="{'is-active': isSelected(allOptions.slice(-1)[0]) }"
                :title="title"
                @click="selectLast()"
            >
                {{ allOptions.slice(-1)[0].toString() }}
            </a>
            <a
                v-if="allowMultiple && (needsNext || needsPrev)"
                class="kgr-pagination__item kgr-pagination__item_custom"
            >
                ({{ value.length }})
            </a>
        </nav>
    </div>
</template>

<script>
import Icon from '../../icon/Icon.vue';
import MonthOfYear from '../../../../js/date/MonthOfYear';

export default {
    components: {
        'kgr-icon': Icon,
    },
    props: {
        value: {
            type: [MonthOfYear, Array],
            required: true,
        },
        selectable: {
            type: Array,
            required: false,
            default: null,
        },
        allowMultiple: {
            type: Boolean,
            required: false,
            default: false,
        },
        start: {
            type: MonthOfYear,
            required: false,
            default: null,
        },
        end: {
            type: MonthOfYear,
            required: false,
            default: null,
        },
        maxVisible: {
            type: Number,
            required: false,
            default: 5,
        },
    },
    data() {
        return {
            centered: null,
            centeredIndex: 0,
            maxVisibleIndex: 0,
            minVisibleIndex: 0,
            needsNext: false,
            needsPrev: false,
            multiSelecting: false,
        }
    },
    computed: {
        allOptions() {
            const start = this.start ?? new MonthOfYear(1, new Date().getFullYear());
            const end = this.end ?? new MonthOfYear(12, new Date().getFullYear());
            if (start.isAfter(end)) {
                console.error('start must not be after end');
                return [];
            }
            let options = [start];
            let current = start.next();
            while (!current.isAfter(end)) {
                options.push(current);
                current = current.next();
            }
            return options;
        },
        options() {
            let options = this.allOptions;
            if (options.length > this.maxVisible) {
                const maxVisibleIndex = this.maxVisibleIndex;
                const minVisibleIndex = this.minVisibleIndex;
                this.needsPrev = minVisibleIndex > 0;
                this.needsNext = maxVisibleIndex < options.length - 1;
                options = options.filter((monthOfYear, b) => {
                    return b >= minVisibleIndex && b <= maxVisibleIndex;
                });
            }

            return options;
        },
        title() {
            return this.allowMultiple ? this.$t('Strg-Taste (Windows) / Cmd-Taste (Mac) gedrückt halten für Mehrfachauswahl') : false;
        }
    },
    created() {
        const more = Math.floor(this.maxVisible / 2);
        const centeredIndex = this.getDefaultCenteredIndex();
        this.maxVisibleIndex = Math.max(Math.min(centeredIndex + more, this.allOptions.length - 1), this.maxVisible - 1);
        this.minVisibleIndex = this.maxVisibleIndex - this.maxVisible + 1;

        if (this.allowMultiple && !Array.isArray(this.value)) {
            console.error('value must be an array')
            return;
        }
        if (!this.allowMultiple && this.value !== null && !this.value instanceof MonthOfYear) {
            console.error('value must be null or an instance of MonthOfYear')
            return;
        }

        if (this.allowMultiple) {
            const component = this;
            const multiSelectingHandler = function (e) {
                if (e.which !== 17 && e.which !== 91 && e.which !== 93 && e.which !== 224) {
                    return;
                }
                if (e.type === 'keydown') {
                    component.multiSelecting = true;
                } else if (e.type === 'keyup') {
                    component.multiSelecting = false;
                }
            }
            window.addEventListener('keyup', multiSelectingHandler);
            window.addEventListener('keydown', multiSelectingHandler);
        }
    },
    methods: {
        isSelected(monthOfYear) {
            if (this.allowMultiple) {
                return this.value.find(p => p.equals(monthOfYear))
            }
            return this.value !== null && this.value.equals(monthOfYear)
        },
        selectAll() {
            this.minVisibleIndex = 0;
            this.maxVisibleIndex = this.maxVisible - 1;
            this.$emit('input', this.allOptions)
            this.$nextTick(() => {
                this.$emit('post-input', this.allOptions);
            });
        },
        select(monthOfYear) {
            if (!this.allowMultiple) {
                this.$emit('input', monthOfYear)
                this.$nextTick(() => {
                    this.$emit('post-input', monthOfYear);
                });
                return;
            }
            if (this.multiSelecting) {
                if (this.value.indexOf(monthOfYear) === -1) {
                    const value = this.value.concat([monthOfYear])
                    this.$emit('input', value)
                    this.$nextTick(() => {
                        this.$emit('post-input', value);
                    });
                } else {
                    const value = this.value.filter(moy => !monthOfYear.equals(moy))
                    this.$emit('input', value)
                    this.$nextTick(() => {
                        this.$emit('post-input', value);
                    });
                }
            } else {
                this.$emit('input', [monthOfYear])
                this.$nextTick(() => {
                    this.$emit('post-input', [monthOfYear]);
                });
            }
        },
        selectFirst() {
            if (!this.allowMultiple) {
                this.minVisibleIndex = 0;
                this.maxVisibleIndex = this.maxVisible - 1;
            }
            this.select(this.allOptions[0])
        },
        selectLast() {
            if (!this.allowMultiple) {
                this.maxVisibleIndex = this.allOptions.length - 1;
                this.minVisibleIndex = this.maxVisibleIndex - this.maxVisible + 1;
            }
            this.select(this.allOptions.slice(-1)[0])
        },
        next() {
            this.minVisibleIndex++;
            this.maxVisibleIndex++;
        },
        prev() {
            this.minVisibleIndex--;
            this.maxVisibleIndex--;
        },
        getDefaultCenteredIndex() {
            let option = MonthOfYear.current();
            if (this.allowMultiple) {
                option = this.value.length > 0 ? this.value.slice(-1)[0] : option;
            } else if(this.value !== null) {
                option = this.value;
            }
            const optionIndex = this.allOptions.findIndex(o => o.equals(option));
            if (optionIndex === -1) {
                return Math.floor(this.maxVisible / 2);
            }
            return optionIndex;
        },
    }
}
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
.kgr-pagination__item.kgr-pagination__item_custom {
    width: 4em !important;
    margin-left: 3px !important;
}
.kgr-pagination__item.kgr-pagination__item_nav {
    width: 2em !important;
    margin-left: 3px !important;
}
</style>

